import { Box, Skeleton, Stack, Typography } from '@mui/material';
import {
  AnalyticsEventName,
  AssessmentAnalyticsProvider,
  trackAmplitudeEvent,
} from '@schooly/analytics';
import {
  AssessmentsGrade,
  AssessmentWithEntries,
  FilterKeys,
  GetAssessmentsEntriesSort,
  SchoolYear,
  SORT_DIRECTION,
  useGetAssessmentsEntriesQuery,
  useGetAssessmentsGradesForSchoolQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  ArrangeByDropdown,
  AssessmentsArrangeBy,
  getArrangedAssessmentsEntries,
} from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS } from '@schooly/constants';
import { useAssessmentLabels } from '@schooly/hooks/use-assessment-labels';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import {
  Counter,
  ExpandableSearch,
  GridRowCell,
  GridRowItem,
  GridRowName,
  GridRowStyled,
  randomInt,
} from '@schooly/style';
import { DropdownYears } from '@schooly/style';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { NoSearchResultsFound } from '../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import { ListViewAssessmentEntries } from '../../../components/uikit-components/ListViewAssessmentEntries/ListViewAssessmentsEntries';
import { useCollapsiblePanel } from '../../../context/collapsiblePanel/useCollapsiblePanel';
import { useProfile } from '../../../context/profile/useProfile';
import useSchoolYears from '../../../hooks/useSchoolYears';
import { ProfileModalAssessmentsExport } from './ProfileModalAssessmentsExport';
import { ProfileModalAssessmentsSubjectFilter } from './ProfileModalAssessmentsSubjectFilter';

const PAGE_SIZE = 30;
const SKELETON_ROWS_COUNT = 6;

export const SEARCH_DEBOUNCE_WAIT = 200;
export const STUDENT_ASSESSMENTS_ARRANGE_BY_FILTER_KEYS = [
  FilterKeys.Date,
  FilterKeys.Subject,
  FilterKeys.AreaOfLearning,
] as const;

const DEFAULT_STUDENT_ASSESSMENTS_SORT: GetAssessmentsEntriesSort[] = [
  { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
  { columnTextId: 'group_type', direction: SORT_DIRECTION.DESC },
  { columnTextId: 'subject_order', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'group_name', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
];

export const ProfileModalAssessments: FC = () => {
  const { schoolMembership } = useProfile();
  const { $t } = useIntl();
  const { getAssessmentsArrangeByTitle } = useAssessmentLabels();

  const arrangeByDropdown = useRef<ArrangeByDropdown | null>(null);
  const [arrangeBy, setArrangeBy] = useState<AssessmentsArrangeBy>(
    STUDENT_ASSESSMENTS_ARRANGE_BY_FILTER_KEYS[0],
  );

  const { state } = useCollapsiblePanel();
  const isPanelOpen = state.isOpen;

  const { schoolId = '' } = useAuth();
  const { defaultValidity, schoolYears } = useSchoolYears();

  const relationId = schoolMembership?.relation_id || '';

  const { data: grades, isLoading: gradesLoading } = useGetAssessmentsGradesForSchoolQuery(
    { schoolId },
    { refetchOnMount: 'always', enabled: !!schoolId },
  );

  const {
    data,
    isLoading: assessmentsLoading,
    hasNextPage,
    params,
    setParams,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetAssessmentsEntriesQuery(
    {
      schoolId,
      pageSize: PAGE_SIZE,
      sort: DEFAULT_STUDENT_ASSESSMENTS_SORT,
      query: '',
      filters: {
        [FilterKeys.Relation]: [relationId],
        [FilterKeys.Date]: defaultValidity
          ? [defaultValidity?.start, defaultValidity?.end]
          : undefined,
        [FilterKeys.Subject]: [],
      },
    },
    { refetchOnMount: 'always', enabled: !!schoolId && !!relationId },
  );
  const total = data?.pages[0]?.count;

  const isLoading = gradesLoading || assessmentsLoading;

  const handleSetSubjectIds = useCallback(
    (subjectIds: string[]) => {
      setParams((p) => ({
        ...p,
        filters: { ...p.filters, [FilterKeys.Subject]: subjectIds },
      }));
    },
    [setParams],
  );

  const handleChangeSort = useCallback(
    (sortBy: GetAssessmentsEntriesSort['columnTextId']) => {
      let newSort = DEFAULT_STUDENT_ASSESSMENTS_SORT;
      switch (sortBy) {
        case 'group_type':
          newSort = [
            { columnTextId: 'group_type', direction: SORT_DIRECTION.DESC },
            { columnTextId: 'subject_order', direction: SORT_DIRECTION.ASC },
            { columnTextId: 'group_name', direction: SORT_DIRECTION.ASC },
            { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
            { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
            { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
            { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
          ];
          break;
        case 'evaluation_type':
          newSort = [
            { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
            { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
            { columnTextId: 'group_type', direction: SORT_DIRECTION.DESC },
            { columnTextId: 'subject_order', direction: SORT_DIRECTION.ASC },
            { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
            { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
          ];
          break;
      }

      setParams((p) => ({ ...p, sort: newSort }));
    },
    [setParams],
  );

  const handleArrangeByChange = useCallback(
    (option: AssessmentsArrangeBy) => {
      let arrangementType: 'date' | 'subject' | 'area_of_learning' = 'date';

      setArrangeBy(option);
      if (option === FilterKeys.Date) {
        handleChangeSort('assessment_date');
      } else if (option === FilterKeys.Subject) {
        handleChangeSort('group_type');
        arrangementType = 'subject';
      } else {
        handleChangeSort('evaluation_type');
        arrangementType = 'area_of_learning';
      }

      trackAmplitudeEvent({
        name: AnalyticsEventName.AssessmentChangeStudentArrangement,
        payload: {
          arrangement_type: arrangementType,
        },
      });
    },
    [handleChangeSort],
  );

  const entries = useMemo(() => {
    const assessments =
      data?.pages.reduce<AssessmentWithEntries[]>((prev, curr) => {
        prev.push(...curr.results);
        return prev;
      }, []) ?? [];

    return getArrangedAssessmentsEntries(assessments, arrangeBy);
  }, [data?.pages, arrangeBy]);

  const lists = useMemo(() => {
    return grades?.reduce(
      (prev, list) => {
        if (list) {
          prev[list.id] = list;
        }

        return prev;
      },
      {} as Record<string, AssessmentsGrade>,
    );
  }, [grades]);

  const handleSchoolYearChange = useCallback(
    (year: SchoolYear) => {
      setParams((p) => {
        return {
          ...p,
          filters: { ...p.filters, [FilterKeys.Date]: [year.start, year.end] },
        };
      });
    },
    [setParams],
  );

  const handleChangeQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
    },
    [setParams],
  );

  const selectedSchoolYear = useMemo(
    () =>
      schoolYears.find(
        (y) =>
          y.start === params.filters?.[FilterKeys.Date]?.[0] &&
          y.end === params.filters?.[FilterKeys.Date]?.[1],
      ),
    [params.filters, schoolYears],
  );

  const totalCount = data?.pages[0].count ?? 0;

  const loaderRef = useInfiniteScroll(
    isLoading || isFetchingNextPage || !hasNextPage,
    fetchNextPage,
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between" gap={2.5} mb={2.5}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h2">
            <FormattedMessage id="section-Assessments" />
          </Typography>
          {!!totalCount && <Counter>{totalCount}</Counter>}
        </Stack>
        <Stack direction="row" alignItems="center" gap={2.5} mt={-0.75}>
          <ExpandableSearch
            value={params.query || ''}
            onChange_MemoizedCallbackOnly={handleChangeQuery}
            placeholder={$t({ id: 'people-Search' })}
            withDebounce
            sx={{ position: 'absolute', right: 0 }}
          />

          <ArrangeByDropdown
            ref={(r: any) => (arrangeByDropdown.current = r as ArrangeByDropdown)}
            onSelectOption={handleArrangeByChange}
            selectedOption={arrangeBy}
            options={STUDENT_ASSESSMENTS_ARRANGE_BY_FILTER_KEYS}
            renderLabel={() => (
              <>
                <Typography variant="h3" color="text.secondary" noWrap>
                  {$t({ id: 'filter-ArrangeBy' })}:
                </Typography>
                <Typography variant="h3" color="text.primary" noWrap>
                  {$t({ id: PROPERTIES_TEXT_IDS[arrangeBy] })}
                </Typography>
              </>
            )}
          />

          {!!schoolId && (
            <ProfileModalAssessmentsSubjectFilter
              subjectIds={params.filters?.[FilterKeys.Subject] || []}
              onSetSubjectIds={handleSetSubjectIds}
              schoolId={schoolId}
            />
          )}
          <DropdownYears
            years={schoolYears}
            defaultYear={defaultValidity}
            currentYear={selectedSchoolYear}
            onYearChange={handleSchoolYearChange}
          />

          <ProfileModalAssessmentsExport />
        </Stack>
      </Stack>

      {isLoading ? (
        <>
          <Typography variant="h3" mb={0.5}>
            <Skeleton width={30} />
          </Typography>
          <ProfileModalAssessmentsListSkeleton rowCount={SKELETON_ROWS_COUNT} />
        </>
      ) : (
        <AssessmentAnalyticsProvider source="studentProfile" grades={grades ?? []}>
          <Stack
            sx={(theme) => ({
              overflowY: 'auto',
              height: '100%',
              mb: -2.5,
              pb: 2.5,
              gap: 2.5,
              [theme.breakpoints.down('md')]: { mr: -2.5 },
            })}
          >
            {totalCount === 0 && <NoSearchResultsFound type="small" />}
            {entries.map(([key, assessments]) => (
              <Box key={key}>
                {arrangeBy === FilterKeys.Date && (
                  <Typography variant="h4" color="common.grey3">
                    {getAssessmentsArrangeByTitle(arrangeBy, assessments[0])}
                  </Typography>
                )}

                <ListViewAssessmentEntries
                  assessments={assessments}
                  lists={lists}
                  relationId={schoolMembership?.relation_id ?? ''}
                  fullView={!isPanelOpen}
                  arrangeBy={arrangeBy}
                />
              </Box>
            ))}

            {hasNextPage && (
              <>
                {isFetchingNextPage && (
                  <ProfileModalAssessmentsListSkeleton
                    rowCount={Math.min(
                      PAGE_SIZE,
                      total && data ? total - data.pages.length * PAGE_SIZE : PAGE_SIZE,
                    )}
                  />
                )}
                <Box ref={loaderRef} />
              </>
            )}
          </Stack>
        </AssessmentAnalyticsProvider>
      )}
    </>
  );
};

const ProfileModalAssessmentsListSkeleton = ({ rowCount }: { rowCount: number }) => {
  return (
    <Box>
      {[...new Array(rowCount)].map((_, i) => {
        return (
          <GridRowStyled sx={{ pointerEvents: 'none', overflow: 'hidden' }} key={i}>
            <GridRowItem gap={1} sx={{ padding: 1.25 }}>
              <GridRowName minWidth={200} variant={'h3'}>
                <Skeleton width={`${randomInt(50, 100)}%`} />
              </GridRowName>
              <GridRowCell minWidth={210} variant={'h3'}>
                <Skeleton width={`${randomInt(50, 100)}%`} />
              </GridRowCell>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
            </GridRowItem>
          </GridRowStyled>
        );
      })}
    </Box>
  );
};
